import React, { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import Icon from '../../components/AppIcon';
import ExpertCard from './components/ExpertCard';
import FilterPanel from './components/FilterPanel';
import ExpertProfileModal from './components/ExpertProfileModal';
import ContractModal from './components/ContractModal';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { getUserCases } from '../../services/caseService';
import { normaliseCertifications } from '../../utils/certifications';

// ── Skeleton card shown while loading ──────────────────────────────────────
const SkeletonCard = () => (
  <div className="bg-card border border-border rounded-2xl p-5 animate-pulse">
    <div className="flex gap-3.5 mb-4">
      <div className="w-14 h-14 bg-muted rounded-xl flex-shrink-0" />
      <div className="flex-1 space-y-2 pt-1">
        <div className="h-3.5 bg-muted rounded w-2/3" />
        <div className="h-3 bg-muted rounded w-1/2" />
        <div className="h-3 bg-muted rounded w-3/4" />
      </div>
    </div>
    <div className="flex gap-1.5 mb-4">
      <div className="h-5 bg-muted rounded-full w-20" />
      <div className="h-5 bg-muted rounded-full w-16" />
    </div>
    <div className="grid grid-cols-3 border border-muted rounded-xl overflow-hidden mb-4">
      {[0, 1, 2].map((i) => (
        <div key={i} className="py-3 px-2 flex flex-col items-center gap-1">
          <div className="h-4 bg-muted rounded w-8" />
          <div className="h-2.5 bg-muted rounded w-10" />
        </div>
      ))}
    </div>
    <div className="flex gap-2">
      <div className="flex-1 h-8 bg-muted rounded-lg" />
      <div className="flex-1 h-8 bg-muted rounded-lg" />
    </div>
  </div>
);

// ── Normalize DB row to shape ExpertCard / ProfileModal expect ──────────────
const normalize = (expert) =>
  expert
    ? {
        id: expert.id,
        user_id: expert.user_id,
        name: expert.full_name,
        title: expert.title,
        avatar: expert.avatar_url || null,
        avatarAlt: `${expert.full_name} profile photo`,
        verified: expert.verified,
        rating: expert.average_rating || 0,
        reviewCount: 0,
        hourlyRate: expert.hourly_rate,
        experienceYears: expert.years_experience,
        casesCompleted: expert.cases_completed,
        availability: 'available',
        certifications: normaliseCertifications(expert.certifications),
        specialties: expert.specialties || [],
        bio: expert.bio,
        education: expert.education || [],
        testimonials: expert.testimonials || [],
        workExperiences: expert.work_experiences || [],
      }
    : null;

// ── Main page ───────────────────────────────────────────────────────────────
const ExpertMarketplaceListing = () => {
  const { user, userRole } = useAuth();
  const navigate = useNavigate();

  const [experts, setExperts] = useState([]);
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('rating');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isContractOpen, setIsContractOpen] = useState(false);

  const [filters, setFilters] = useState({
    specialties: [],
    certifications: [],
    rateRange: { min: '', max: '' },
    minExperience: '',
    verifiedOnly: false,
  });

  // ── Data loading ──────────────────────────────────────────────────────────
  useEffect(() => { loadExperts(); }, []);
  useEffect(() => { if (user?.id) loadCases(); }, [user?.id]);

  const loadExperts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('expert_profiles')
        .select('*')
        .eq('status', 'approved')
        .eq('connect_charges_enabled', true)
        .order('average_rating', { ascending: false, nullsFirst: false });
      if (!error && data?.length > 0) {
        const userIds = data.map(e => e.user_id);
        const { data: weData } = await supabase
          .from('expert_work_experiences')
          .select('*')
          .in('user_id', userIds)
          .order('start_year', { ascending: false });
        const weByUserId = {};
        (weData || []).forEach(we => {
          if (!weByUserId[we.user_id]) weByUserId[we.user_id] = [];
          weByUserId[we.user_id].push(we);
        });
        setExperts(data.map(e => ({ ...e, work_experiences: weByUserId[e.user_id] || [] })));
      } else {
        setExperts(data || []);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCases = async () => {
    const result = await getUserCases(user.id);
    if (result?.success) setCases(result.data || []);
  };

  // ── Filtering & sorting ───────────────────────────────────────────────────
  const filteredExperts = useMemo(() => {
    let r = [...experts];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      r = r.filter(
        (e) =>
          e.full_name?.toLowerCase().includes(q) ||
          e.title?.toLowerCase().includes(q) ||
          e.specialties?.some((s) => s.toLowerCase().includes(q))
      );
    }
    if (filters.verifiedOnly) r = r.filter((e) => e.verified);
    if (filters.rateRange.min) r = r.filter((e) => (e.hourly_rate ?? 0) >= parseFloat(filters.rateRange.min));
    if (filters.rateRange.max) r = r.filter((e) => (e.hourly_rate ?? 0) <= parseFloat(filters.rateRange.max));
    if (filters.minExperience) r = r.filter((e) => (e.years_experience ?? 0) >= parseInt(filters.minExperience));
    if (filters.specialties.length)
      r = r.filter((e) => filters.specialties.some((s) => e.specialties?.includes(s)));
    if (filters.certifications.length)
      r = r.filter((e) => {
        const certNames = normaliseCertifications(e.certifications).map(c => c.name.toLowerCase());
        return filters.certifications.some((f) => certNames.some((n) => n.includes(f.toLowerCase())));
      });

    if (sortBy === 'rating')    r.sort((a, b) => (b.average_rating ?? 0) - (a.average_rating ?? 0));
    if (sortBy === 'rate-low')  r.sort((a, b) => (a.hourly_rate ?? 0) - (b.hourly_rate ?? 0));
    if (sortBy === 'rate-high') r.sort((a, b) => (b.hourly_rate ?? 0) - (a.hourly_rate ?? 0));
    if (sortBy === 'experience') r.sort((a, b) => (b.years_experience ?? 0) - (a.years_experience ?? 0));
    if (sortBy === 'cases')     r.sort((a, b) => (b.cases_completed ?? 0) - (a.cases_completed ?? 0));

    return r;
  }, [experts, searchQuery, filters, sortBy]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const handleFilterChange = (key, value) => setFilters((p) => ({ ...p, [key]: value }));

  const clearFilters = () => {
    setFilters({ specialties: [], certifications: [], rateRange: { min: '', max: '' }, minExperience: '', verifiedOnly: false });
    setSearchQuery('');
  };

  const openProfile = (rawExpert) => { setSelectedExpert(rawExpert); setIsProfileOpen(true); };
  const openContract = (rawExpert) => { setSelectedExpert(rawExpert); setIsContractOpen(true); };

  const activeFilterCount =
    filters.specialties.length +
    filters.certifications.length +
    (filters.rateRange.min ? 1 : 0) +
    (filters.rateRange.max ? 1 : 0) +
    (filters.minExperience ? 1 : 0) +
    (filters.verifiedOnly ? 1 : 0);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Helmet>
        <title>Expert Marketplace — FireCaseIQ</title>
        <meta
          name="description"
          content="Browse and engage verified fire forensics experts for professional case analysis and testimony."
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Sidebar userRole={userRole} />

        <div className="lg:ml-60 min-h-screen">

          {/* ── Standard gradient header (matches expert dashboard) ── */}
          <div className="bg-gradient-to-r from-primary to-secondary text-white p-6 md:p-8">
            <div className="max-w-7xl mx-auto flex items-start justify-between gap-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => {
                    const isCollapsed = document.body.classList.toggle('sidebar-collapsed');
                    localStorage.setItem('sidebarCollapsed', isCollapsed.toString());
                  }}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  aria-label="Toggle sidebar"
                >
                  <Icon name="PanelLeft" size={24} />
                </button>
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold mb-1">Expert Marketplace</h1>
                  <p className="text-white/80 text-sm md:text-base">
                    Browse verified fire forensics experts and send a contract offer
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="[&>div>button]:text-white [&>div>button:hover]:bg-white/10">
                  <NotificationCenter userRole={userRole} />
                </div>
              </div>
            </div>
          </div>

          {/* ── Search + filter toolbar ── */}
          <div className="border-b border-border bg-card px-4 md:px-8 py-3">
            <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
              {/* Search bar */}
              <div className="relative w-full max-w-xl">
                <Icon
                  name="Search"
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                />
                <input
                  type="search"
                  placeholder="Search by name, specialty, or certification…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-9 py-2 text-sm bg-background border border-input rounded-lg placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-shadow"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5"
                    aria-label="Clear search"
                  >
                    <Icon name="X" size={13} />
                  </button>
                )}
              </div>

              {/* Mobile filter button */}
              <button
                className="lg:hidden relative flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-background border border-input rounded-lg hover:bg-muted transition-colors"
                onClick={() => setIsFilterOpen(true)}
              >
                <Icon name="SlidersHorizontal" size={14} />
                <span>Filters</span>
                {activeFilterCount > 0 && (
                  <span className="ml-0.5 text-[10px] font-bold bg-primary text-primary-foreground rounded-full w-4 h-4 flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          <main className="flex flex-col">
            {/* ── Content area: filters + grid ── */}
            <div className="flex flex-1 gap-0">

              {/* Desktop filter sidebar */}
              <aside className="hidden lg:block w-64 xl:w-72 flex-shrink-0 border-r border-border p-4">
                <FilterPanel
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={clearFilters}
                  isOpen={false}
                  onClose={() => {}}
                  resultCount={filteredExperts.length}
                />
              </aside>

              {/* Mobile filter drawer — hidden on desktop (aside handles it there) */}
              <div className="lg:hidden">
                <FilterPanel
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={clearFilters}
                  isOpen={isFilterOpen}
                  onClose={() => setIsFilterOpen(false)}
                  resultCount={filteredExperts.length}
                />
              </div>

              {/* Results column */}
              <div className="flex-1 min-w-0 p-4 md:p-6">

                {/* Results toolbar */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-muted-foreground">
                      {loading ? (
                        <span className="inline-block w-20 h-4 bg-muted rounded animate-pulse" />
                      ) : (
                        <>
                          <span className="font-bold text-foreground">{filteredExperts.length}</span>{' '}
                          expert{filteredExperts.length !== 1 ? 's' : ''} found
                        </>
                      )}
                    </span>

                    {/* Active filter chips */}
                    {filters.verifiedOnly && (
                      <FilterChip label="Verified only" onRemove={() => handleFilterChange('verifiedOnly', false)} />
                    )}
                    {filters.specialties.map((s) => (
                      <FilterChip
                        key={s}
                        label={s}
                        onRemove={() => handleFilterChange('specialties', filters.specialties.filter((x) => x !== s))}
                      />
                    ))}
                    {filters.certifications.map((c) => (
                      <FilterChip
                        key={c}
                        label={c}
                        onRemove={() => handleFilterChange('certifications', filters.certifications.filter((x) => x !== c))}
                      />
                    ))}
                    {(filters.rateRange.min || filters.rateRange.max) && (
                      <FilterChip
                        label={`$${filters.rateRange.min || '0'}–${filters.rateRange.max ? '$' + filters.rateRange.max : '∞'}`}
                        onRemove={() => handleFilterChange('rateRange', { min: '', max: '' })}
                      />
                    )}
                    {filters.minExperience && (
                      <FilterChip
                        label={`≥${filters.minExperience} yrs`}
                        onRemove={() => handleFilterChange('minExperience', '')}
                      />
                    )}
                    {activeFilterCount > 0 && (
                      <button
                        onClick={clearFilters}
                        className="text-xs text-muted-foreground hover:text-destructive transition-colors underline underline-offset-2"
                      >
                        Clear all
                      </button>
                    )}
                  </div>

                  {/* Sort */}
                  <div className="flex items-center gap-2">
                    <Icon name="ArrowUpDown" size={13} className="text-muted-foreground" />
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="text-sm bg-background border border-input rounded-xl px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="rating">Top Rated</option>
                      <option value="rate-low">Rate: Low → High</option>
                      <option value="rate-high">Rate: High → Low</option>
                      <option value="experience">Most Experienced</option>
                      <option value="cases">Most Cases</option>
                    </select>
                  </div>
                </div>

                {/* Grid */}
                {loading ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
                  </div>
                ) : filteredExperts.length === 0 ? (
                  <EmptyState onClear={clearFilters} hasFilters={activeFilterCount > 0 || !!searchQuery} />
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filteredExperts.map((expert) => (
                      <ExpertCard
                        key={expert.id}
                        expert={normalize(expert)}
                        onViewProfile={() => openProfile(expert)}
                        onRequestQuote={userRole !== 'expert' ? () => openContract(expert) : undefined}
                        onMessage={userRole !== 'expert' ? () => navigate(`/client-dashboard/messages?userId=${expert.user_id}`) : undefined}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>

        {/* ── Modals ── */}
        <ExpertProfileModal
          expert={normalize(selectedExpert)}
          isOpen={isProfileOpen}
          onClose={() => setIsProfileOpen(false)}
          onRequestQuote={userRole !== 'expert' ? () => { setIsProfileOpen(false); setIsContractOpen(true); } : undefined}
          currentUserId={user?.id}
        />

        <ContractModal
          expert={selectedExpert}
          cases={cases}
          clientId={user?.id}
          isOpen={isContractOpen}
          onClose={() => setIsContractOpen(false)}
        />
      </div>
    </>
  );
};

// ── Small helpers ─────────────────────────────────────────────────────────────
const FilterChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-primary/10 text-primary border border-primary/20 rounded-full px-2.5 py-0.5">
    {label}
    <button onClick={onRemove} className="hover:text-primary/70 transition-colors" aria-label={`Remove ${label} filter`}>
      <Icon name="X" size={10} />
    </button>
  </span>
);

const EmptyState = ({ onClear, hasFilters }) => (
  <div className="flex flex-col items-center justify-center py-24 text-center">
    <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
      <Icon name="SearchX" size={28} className="text-muted-foreground" />
    </div>
    <h3 className="text-lg font-bold text-foreground mb-1">No experts found</h3>
    <p className="text-sm text-muted-foreground max-w-xs mb-5">
      {hasFilters
        ? 'Try broadening your filters or clearing your search.'
        : 'No verified experts are available right now. Check back soon.'}
    </p>
    {hasFilters && (
      <button
        onClick={onClear}
        className="text-sm font-medium text-primary hover:underline"
      >
        Clear all filters
      </button>
    )}
  </div>
);

export default ExpertMarketplaceListing;
